/**
 * How deep the first page from each library should be.
 *
 * The merge frontier is a MINIMUM: `getEmitFrontier` takes the earliest-in-sort-order
 * `lastFetchedRank` across every non-exhausted cursor, and nothing past it may be
 * emitted. So the depth the view can reach is decided by ONE library — whichever has
 * the densest recent activity — while the page size is paid by all N.
 *
 * That is why a uniformly larger page is the wrong lever: it costs N× to relieve a
 * constraint set 1×, and on a wide site it also blows the total-row cap during the very
 * first fan-out (N × pageSize), capping the view before the user has seen anything.
 *
 * Instead the page size falls out of the library count against a fixed row budget, so:
 *   - a few large libraries  -> deep pages, which is exactly where depth is cheap and
 *                               the frontier most needs relieving;
 *   - a wide site            -> shallow pages, bounding the initial fan-out.
 *
 * Load-more is unaffected in shape: it advances only the cursors tied at the frontier
 * (`getFrontierListIds`), so it reuses the same per-cursor ViewXml and fetches one or
 * two pages, not N.
 */

/**
 * Hard ceiling on rows BUFFERED across every cursor — the whole merge buffer, not what
 * is on screen. Lives here beside the page budget because the two are one concern: the
 * initial fan-out is N × pageSize, so the page policy is what has to keep clear of this.
 *
 * Re-exported by useAggregatedDocuments for existing importers; defined here so the
 * policy can be reasoned about, and tested, without the hook's auth/axios chain.
 */
export const AGGREGATED_MAX_TOTAL_ROWS = 5000;

/**
 * Target rows for the whole initial fan-out. Well above a viewport (~30-50 rows) so the
 * frontier clears the first screen even when one library dominates recent activity, and
 * far below AGGREGATED_MAX_TOTAL_ROWS so no library count can trip the cap on load.
 */
export const AGGREGATED_INITIAL_ROW_BUDGET = 720;

/**
 * Floor: below this a page stops being a useful frontier probe, and the per-request
 * overhead starts to dominate the rows returned. A site wide enough to hit the floor
 * (>24 libraries) exceeds the budget by design — the alternative is pages too thin to
 * emit anything.
 */
export const AGGREGATED_MIN_PAGE_SIZE = 30;

/**
 * Ceiling. Past this the extra depth is speculative: it is only reachable by scrolling,
 * which load-more already serves on demand and more cheaply, because it fetches for the
 * blocking cursor alone.
 */
export const AGGREGATED_MAX_PAGE_SIZE = 240;

/**
 * Hard ceiling on the initial fan-out (N × pageSize), which OVERRIDES the floor.
 *
 * Half the buffer, so load-more still has room to page afterwards. The floor is a
 * quality preference; the cap is a correctness constraint — tripping it on load marks
 * every cursor exhausted, which permanently disables paging for that view. Thin pages
 * are worse than nothing; a view that can never page again is worse than thin pages.
 *
 * This only binds above ~83 libraries. Below that the floor is the tighter of the two.
 */
export const AGGREGATED_INITIAL_FANOUT_CEILING = Math.floor(AGGREGATED_MAX_TOTAL_ROWS / 2);

/**
 * Rows to request per library on the initial fan-out.
 *
 * @param libraryCount libraries taking part in this load (after read/filter exclusions,
 *                     NOT the site total — excluded libraries issue no request and must
 *                     not shrink everyone else's page).
 */
export const resolveAggregatedPageSize = (libraryCount: number): number => {
    // Guard the divide: a non-positive or non-finite count means no fan-out is coming,
    // so the value is unused — return the ceiling rather than Infinity or NaN.
    if (!Number.isFinite(libraryCount) || libraryCount <= 0) {
        return AGGREGATED_MAX_PAGE_SIZE;
    }

    const budgeted = Math.round(AGGREGATED_INITIAL_ROW_BUDGET / libraryCount);
    const preferred = Math.min(AGGREGATED_MAX_PAGE_SIZE, Math.max(AGGREGATED_MIN_PAGE_SIZE, budgeted));

    // The fan-out ceiling wins over the floor. `floor` (never `round`) so N × pageSize
    // cannot round back above the ceiling, and at least 1 row so an absurd library count
    // still issues a real request instead of an empty RowLimit.
    const affordable = Math.max(1, Math.floor(AGGREGATED_INITIAL_FANOUT_CEILING / libraryCount));
    return Math.min(preferred, affordable);
};

/**
 * Rows to request per CLIENT-GROUP library, whose first pages are fetched after the site's
 * own libraries have loaded (and been shown).
 *
 * The share is the one every library would have had if all of them had loaded together, so
 * adding group sites thins the group pages the way a wider site thins its own. It is also
 * bounded by what the first stage left of the fan-out ceiling: the rows already buffered are
 * spent, and the two stages together should still leave load-more room to page.
 *
 * Unlike the first stage, the FLOOR wins over that ceiling here, and only the hard row cap
 * thins pages below it. The size sticks to the cursor for every later load-more, and the
 * ceiling can already be spent when the group joins (the user paged deep first, or a wide
 * site): a 1-row page would then page that library one row per request for the rest of the
 * view. Overshooting the ceiling by floor-sized pages still leaves load-more its room.
 *
 * @param totalSourceCount own + group libraries taking part.
 * @param bufferedRows rows already buffered when the group pages are requested.
 * @param groupSourceCount group libraries being requested now.
 */
export const resolveGroupSourcePageSize = (totalSourceCount: number, bufferedRows: number, groupSourceCount: number): number => {
    if (!Number.isFinite(groupSourceCount) || groupSourceCount <= 0) {
        return AGGREGATED_MAX_PAGE_SIZE;
    }
    const buffered = Math.max(0, bufferedRows || 0);
    const preferred = resolveAggregatedPageSize(Math.max(totalSourceCount, groupSourceCount));
    const withinCeiling = Math.floor((AGGREGATED_INITIAL_FANOUT_CEILING - buffered) / groupSourceCount);
    const withinCap = Math.floor((AGGREGATED_MAX_TOTAL_ROWS - buffered) / groupSourceCount);
    const affordable = Math.max(withinCeiling, Math.min(AGGREGATED_MIN_PAGE_SIZE, withinCap));
    return Math.max(1, Math.min(preferred, affordable));
};
